import dxDataGrid, { Column as dxColumn } from 'devextreme/ui/data_grid';
import { exportDataGrid as dxExportToExcel } from 'devextreme/excel_exporter';
import { exportDataGrid as dxExportToPdf, DataGridCell, Cell } from 'devextreme/pdf_exporter';
import { Workbook, Row as xlRow, Cell as xlCell, Column as xlColumn } from 'exceljs';
import { jsPDF } from 'jspdf';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';

import { TExportDataItem, TOrientation } from 'types/gridDefs';
import { TColumns } from 'types/columnDefs';

/* -------------------- */

const setFileExtension = (filename: string, extension: string) => {
    if (filename.toLowerCase().endsWith(extension.toLowerCase())) {
        return filename;
    } else {
        return filename + extension;
    }
};

/* -------------------- */

export const getData = (dataGrid: dxDataGrid<unknown, unknown> | undefined) =>
    () => {
        const items: TExportDataItem[] = dataGrid?.getDataSource().items() ?? [];
        return items;
    };

/* -------------------- */

const parseItemToRows = (item: TExportDataItem, columns: dxColumn[]) => {
    const rows: string[] = [];

    if (item.key && item.items) {
        for (const subItem of item.items as TExportDataItem[]) {
            rows.push(...parseItemToRows(subItem, columns));
        }
    } else {
        const cells: string[] = [];
        for (const col of columns) {
            cells.push(item[col.dataField ?? ''] ?? '');
        }
        rows.push(cells.join(','));
    }
    return rows;
};

export const exportToCsv = (dataGrid: dxDataGrid<unknown, unknown> | undefined) =>
    (filename: string) => {
        if (dataGrid && filename) {
            const columns: dxColumn[] = dataGrid.getVisibleColumns() ?? [];
            const items: TExportDataItem[] = dataGrid.getDataSource().items() ?? [];

            const labels: string[] = [];
            for (const col of columns) {
                labels.push(col.caption ?? col.dataField ?? '');
            }

            const values: string[] = [labels.join(',')];        // initialise values (array of rows) with the labels (header) row

            for (const item of items) {
                const rows = parseItemToRows(item, columns);
                values.push(...rows);
            }

            const buffer = values.join('\n');
            const blob = new Blob([buffer], { type: 'text/plain' });
            saveAs(blob, setFileExtension(filename, '.csv'));
        }
    };

/* -------------------- */

export const generateWorkbook = (dataGrid: dxDataGrid<unknown, unknown> | undefined, columns: TColumns) =>
    async (worksheetName: string, summaryOnly?: boolean) => {
        const workbook = new Workbook();

        const summaryName = worksheetName + ' (Summary)';
        const summary = workbook.addWorksheet(summaryName);
        const worksheet = workbook.addWorksheet(worksheetName);

        await dxExportToExcel({
            component: dataGrid,
            worksheet,
            autoFilterEnabled: false,

            customizeCell: (options: { gridCell?: DataGridCell; excelCell?: any; }) => {
                const { gridCell, excelCell } = options;
                const column = columns?.find(c => c.field === gridCell?.column?.dataField);

                if (gridCell && column?.xlFormat) {
                    if (gridCell?.rowType === 'data' || gridCell?.rowType === 'totalFooter') {
                        excelCell.value = gridCell.value;
                        excelCell.numFmt = column.xlFormat;
                    } else if (gridCell.rowType === 'group' && gridCell.groupSummaryItems) {
                        excelCell.value = gridCell.groupSummaryItems[0].value;
                        excelCell.numFmt = column.xlFormat;
                    } else if (gridCell.rowType === 'header') {
                        excelCell.style = { ...excelCell.style, wrapText: true };
                    }
                }
            }
        });

        if (summaryOnly) {
            // only generating a summary export - show only the header, group & total rows - hide the data rows
            let maxOutlineLevel = 0;
            worksheet.eachRow((row: xlRow) => maxOutlineLevel = Math.max(maxOutlineLevel, row.outlineLevel ?? 0));

            worksheet.eachRow((row: xlRow) => {
                // copy the required rows to the summary worksheet
                if ((row.outlineLevel ?? 0) < maxOutlineLevel) {
                    const sRow = summary.addRow([]);
                    const cells = (row as any)._cells;
                    row.eachCell((cell: xlCell) => {
                        // apply styling to the copied rows
                        const sCell = sRow.getCell(cell.col);
                        sCell.value = cell.value;
                        sCell.style = cell.style;
                        sCell.alignment = cell.alignment;

                        if (cell.isMerged && cell.address !== cell.master.address) {
                            //console.debug("##", cell.address, cell.master.address);
                            const masterCell = summary.getCell(cell.master.address);
                            sCell.merge(masterCell, true);
                        }
                    });
                }
            });

            for (let i = 1; i <= worksheet.actualColumnCount; i++) {
                // apply styling to the copied columns
                const col = worksheet.getColumn(i);
                const sCol = summary.getColumn(i);
                sCol.width = col.width;

                // hide columns that don't have summary content
                const totalCell = worksheet.lastRow?.getCell(i);
                sCol.hidden = !totalCell?.value;
            }

            // remove the header label for the group column and make that column wider
            const cellA1 = summary.getCell('A1');
            cellA1.value = '';
            const columnA = summary.getColumn('A');
            if (columnA.width) {
                columnA.width *= 3;
            }

            // remove the detail worksheet from the workbook
            workbook.removeWorksheet(worksheetName);
        } else {
            // remove the summary worksheet from the workbook
            workbook.removeWorksheet(summaryName);
        }

        return workbook;
    };

export const saveWorkbook = (workbook: Workbook, filename: string) => {
    workbook.xlsx.writeBuffer().then(
        (buffer: ArrayBuffer) => {
            const blob = new Blob([buffer], { type: 'application/octet-stream' });
            saveAs(blob, setFileExtension(filename, '.xlsx'));
        });

};

export const exportToExcel = (dataGrid: dxDataGrid<unknown, unknown> | undefined, columns: TColumns) =>
    async (
        filename: string,
        worksheetName: string,
        summaryOnly?: boolean
    ) => {
        if (dataGrid && filename) {
            try {
                const generateFunc = generateWorkbook(dataGrid, columns);
                const workbook = await generateFunc(worksheetName, summaryOnly);
                saveWorkbook(workbook, filename);
            } catch (e) {
                console.error(e);
            };
        }
    };

/* -------------------- */

type TCustomseCellOptions = {
    gridCell?: DataGridCell;
    pdfCell?: Cell;
};

export const exportToPDF = (dataGrid: dxDataGrid<unknown, unknown> | undefined) =>
    (filename: string, orientation?: TOrientation) => {
        if (dataGrid && filename) {
            const doc = new jsPDF({
                orientation: orientation ?? 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            dxExportToPdf({
                jsPDFDocument: doc,
                component: dataGrid,
                repeatHeaders: true,
                selectedRowsOnly: false,
                customizeCell: ((opts: TCustomseCellOptions) => {
                    const column = opts.gridCell?.column;
                    const pdfCell = opts.pdfCell;
                    if (column && pdfCell && column.type && column.type as string === 'date' && column.format) {
                        const date = dayjs(opts.gridCell?.value as Date);
                        if (date.isValid() && date.valueOf() > 0) {
                            pdfCell.text = date.format(column.format as string);
                        }
                    }
                })
            }).then(() => {
                doc.save(setFileExtension(filename, '.pdf'));
            }).catch((reason: any) => {
                console.error(reason);
            });
        }
    };
